import { useAuthStore } from '../store';
import type {
  NavigationRoutePlanRequest,
  NavigationRoutePlanResponse,
  RouteGraphStatusResponse,
} from '@moorly/shared';

const API_BASE = '/api';

export type OsmFeatureDataset = 'osm' | 'osm_bridges' | 'osm_locks' | 'osm_weirs';
export type OsmFeatureType = 'waterway' | 'bridge' | 'lock' | 'weir';

export interface OSMFeatureSearchResult {
  id: string;
  name: string;
  dataset: OsmFeatureDataset;
  featureType: OsmFeatureType;
  subtype: string | null;
  latitude: number;
  longitude: number;
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | undefined>;
}

function toOptionalUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (!value.trim()) return undefined;
  if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
    return value;
  }
  return undefined;
}

function normalizePost(post: any) {
  if (!post || typeof post !== 'object') return post;

  const reactions =
    Number(post?._count?.reactions ?? post?.stats?.reactions ?? post?.reactionCount ?? 0) || 0;
  const comments =
    Number(post?._count?.comments ?? post?.stats?.comments ?? post?.commentCount ?? 0) || 0;

  const media = Array.isArray(post.media)
    ? post.media.map((item: any) => ({
        ...item,
        url: item?.url || item?.processedUrl || item?.processed_object_key || '',
        thumbnailUrl: item?.thumbnailUrl || item?.thumbnail_object_key,
      }))
    : [];

  const segment =
    post.segment ||
    (post.segmentId
      ? {
          id: post.segmentId,
          name: post.segmentName || '',
          canalName: post.waterwayName,
          waterway: post.waterwayName ? { id: post.waterwayId || '', name: post.waterwayName } : undefined,
        }
      : undefined);

  const weather = post.weather
    ? {
        ...post.weather,
        temp: post.weather.temp ?? post.weather.tempCelsius,
        condition: post.weather.condition ?? post.weather.conditionText,
        icon: post.weather.icon ?? post.weather.conditionCode,
      }
    : post.weatherData;

  return {
    ...post,
    content: post.content ?? post.bodyText ?? '',
    bodyText: post.bodyText ?? post.content ?? '',
    author: post.author || {
      id: post.authorId,
      displayName: post.authorName || 'Unknown',
      handle: post.authorHandle || 'unknown',
      avatarUrl: toOptionalUrl(post.authorAvatarUrl) || toOptionalUrl(post.author?.avatarUrl),
    },
    media,
    segment,
    weather,
    _count: { reactions, comments },
    stats: {
      reactions,
      comments,
      shares: Number(post?.stats?.shares ?? 0) || 0,
    },
  };
}

function normalizeComment(comment: any) {
  if (!comment || typeof comment !== 'object') return comment;
  return {
    ...comment,
    content: comment.content ?? comment.bodyText ?? '',
    bodyText: comment.bodyText ?? comment.content ?? '',
    author: comment.author || {
      id: comment.authorId,
      displayName: comment.authorName || 'Unknown',
      handle: comment.authorHandle || 'unknown',
      avatarUrl: toOptionalUrl(comment.authorAvatarUrl) || toOptionalUrl(comment.author?.avatarUrl),
    },
    replyCount: Number(comment.replyCount ?? 0) || 0,
    replies: Array.isArray(comment.replies) ? comment.replies.map(normalizeComment) : comment.replies,
  };
}

function parsePayloadObject(payload: unknown): Record<string, any> | undefined {
  if (!payload) return undefined;
  if (typeof payload === 'object') return payload as Record<string, any>;
  if (typeof payload !== 'string') return undefined;
  try {
    const parsed = JSON.parse(payload);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function normalizeSegment(segment: any) {
  if (!segment || typeof segment !== 'object') return segment;
  const waterway =
    segment.waterway ||
    (segment.waterwayName || segment.waterwayId
      ? {
          id: segment.waterwayId || '',
          name: segment.waterwayName || '',
        }
      : undefined);
  return {
    ...segment,
    name: segment.name ?? segment.displayName ?? '',
    displayName: segment.displayName ?? segment.name ?? '',
    waterway,
    postCount: Number(segment.postCount ?? 0) || 0,
  };
}

function normalizeChatParticipant(participant: any) {
  if (!participant || typeof participant !== 'object') return participant;
  const user = participant.user || participant;
  return {
    userId: participant.userId ?? user.id ?? '',
    user: {
      id: user.id ?? participant.userId ?? '',
      displayName: user.displayName || user.authorName || 'Unknown',
      handle: user.handle || user.authorHandle || 'unknown',
      avatarUrl: toOptionalUrl(user.avatarUrl) || toOptionalUrl(user.avatarMediaUrl),
    },
  };
}

function normalizeChat(chat: any) {
  if (!chat || typeof chat !== 'object') return chat;
  const participants = Array.isArray(chat.participants)
    ? chat.participants.map(normalizeChatParticipant)
    : [];
  let lastMessage = chat.lastMessage;
  if (typeof chat.lastMessage === 'string') {
    lastMessage = {
      content: chat.lastMessage,
      createdAt: chat.lastMessageAt || chat.createdAt,
      author: { displayName: 'Unknown' },
    };
  } else if (chat.lastMessage && typeof chat.lastMessage === 'object') {
    lastMessage = {
      content: chat.lastMessage.content ?? chat.lastMessage.bodyText ?? '',
      createdAt: chat.lastMessage.createdAt ?? chat.lastMessageAt ?? chat.createdAt,
      author: chat.lastMessage.author || {
        displayName: chat.lastMessage.senderName || chat.lastMessage.authorName || 'Unknown',
      },
    };
  }
  return {
    ...chat,
    unreadCount: Number(chat.unreadCount ?? 0) || 0,
    participants,
    lastMessage,
  };
}

function normalizeMessage(message: any) {
  if (!message || typeof message !== 'object') return message;
  const author = message.author || {
    id: message.authorId ?? message.senderId ?? '',
    displayName: message.authorName ?? message.senderName ?? 'Unknown',
    handle: message.authorHandle ?? message.senderHandle ?? 'unknown',
    avatarUrl: toOptionalUrl(message.authorAvatarUrl) || toOptionalUrl(message.senderAvatarUrl),
  };
  return {
    ...message,
    content: message.content ?? message.bodyText ?? '',
    bodyText: message.bodyText ?? message.content ?? '',
    authorId: message.authorId ?? message.senderId ?? author.id,
    author,
  };
}

function normalizeNotification(notification: any) {
  if (!notification || typeof notification !== 'object') return notification;
  const payload = parsePayloadObject(notification.payload);
  const entityType = notification.entityType ?? notification.targetType;
  const entityId = notification.entityId ?? notification.targetId;
  const data = {
    ...(notification.data || {}),
    ...(payload?.data || {}),
  } as Record<string, string>;
  if (entityType === 'user' && entityId && !data.userId) data.userId = entityId;
  if (entityType === 'post' && entityId && !data.postId) data.postId = entityId;
  if ((entityType === 'chat' || entityType === 'message') && entityId && !data.chatId) data.chatId = entityId;
  return {
    ...notification,
    title: notification.title ?? payload?.title ?? 'Notification',
    body: notification.body ?? payload?.body ?? payload?.message ?? '',
    read: Boolean(notification.read ?? notification.readAt),
    data,
  };
}

function normalizeNotificationPreferences(prefs: any) {
  if (!prefs || typeof prefs !== 'object') {
    return {
      emailNotifications: true,
      pushNotifications: true,
      newFollower: true,
      newMessage: true,
      postReaction: true,
      postComment: true,
      mention: true,
    };
  }
  const postActivity = prefs.postActivityEnabled ?? true;
  return {
    emailNotifications: prefs.emailNotifications ?? prefs.emailEnabled ?? true,
    pushNotifications: prefs.pushNotifications ?? prefs.inAppEnabled ?? true,
    newFollower: prefs.newFollower ?? postActivity,
    newMessage: prefs.newMessage ?? prefs.chatEnabled ?? true,
    postReaction: prefs.postReaction ?? postActivity,
    postComment: prefs.postComment ?? postActivity,
    mention: prefs.mention ?? postActivity,
  };
}

function toNotificationPrefsPayload(data: any) {
  if (!data || typeof data !== 'object') return data;
  if (
    'inAppEnabled' in data ||
    'emailEnabled' in data ||
    'chatEnabled' in data ||
    'postActivityEnabled' in data ||
    'adminActionsEnabled' in data
  ) {
    return data;
  }
  const postFlags = [data.newFollower, data.postReaction, data.postComment, data.mention].filter(
    (value) => typeof value === 'boolean'
  ) as boolean[];
  return {
    inAppEnabled: data.pushNotifications,
    emailEnabled: data.emailNotifications,
    chatEnabled: data.newMessage,
    postActivityEnabled: postFlags.length > 0 ? postFlags.some(Boolean) : undefined,
  };
}

function normalizeAdminAuditEntry(entry: any) {
  if (!entry || typeof entry !== 'object') return entry;
  return {
    ...entry,
    action: entry.action ?? entry.actionType ?? 'unknown',
    actor: entry.actor || {
      displayName: entry.adminName || 'Admin',
      handle: entry.adminHandle || 'admin',
    },
    details: entry.details ?? (entry.reason ? { reason: entry.reason } : undefined),
  };
}

function normalizeAdminUser(user: any) {
  if (!user || typeof user !== 'object') return user;
  return {
    ...user,
    displayName: user.displayName || 'Unknown',
    handle: user.handle || 'unknown',
    postCount: Number(user.postCount ?? 0) || 0,
    reportCount: Number(user.reportCount ?? 0) || 0,
  };
}

function normalizeAdminAd(ad: any) {
  if (!ad || typeof ad !== 'object') return ad;
  return {
    ...ad,
    impressions: Number(ad.impressions ?? 0) || 0,
    clicks: Number(ad.clicks ?? 0) || 0,
  };
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;
  
  let url = `${API_BASE}${endpoint}`;
  if (params) {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== undefined)
    ) as Record<string, string>;
    if (Object.keys(filteredParams).length > 0) {
      const searchParams = new URLSearchParams(filteredParams);
      url += `?${searchParams.toString()}`;
    }
  }
  
  const token = useAuthStore.getState().token;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  let data: any = {};
  const responseLike = response as Response & { text?: () => Promise<string> };
  if (typeof responseLike.text === 'function') {
    const raw = await responseLike.text();
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = {};
      }
    }
  } else {
    try {
      data = await response.json();
    } catch {
      data = {};
    }
  }
  
  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }
    const detailText = typeof data.detail === 'string' && data.detail.trim().length > 0
      ? data.detail.trim()
      : '';
    const hintText = typeof data.hint === 'string' && data.hint.trim().length > 0
      ? data.hint.trim()
      : '';
    const baseMessage = data.error || `Request failed (${response.status})`;
    const message = [
      baseMessage,
      ...(detailText ? [detailText] : []),
      ...(hintText ? [`Hint: ${hintText}`] : []),
    ].join(' | ');

    const apiError = new Error(message) as Error & {
      status?: number;
      endpoint?: string;
      method?: string;
      responseBody?: unknown;
    };
    apiError.name = 'ApiError';
    apiError.status = response.status;
    apiError.endpoint = endpoint;
    apiError.method = typeof fetchOptions.method === 'string'
      ? fetchOptions.method.toUpperCase()
      : 'GET';
    apiError.responseBody = data;
    throw apiError;
  }
  
  return data;
}

// Unified API export that matches what pages expect
export const api = {
  config: {
    getPublic: async () => {
      const res = await request<{ success: boolean; data: { maptilerApiKey: string } }>('/config/public');
      return res.data;
    },
  },
  auth: {
    register: async (data: { email: string; displayName: string; password?: string; handle?: string }) => {
      const res = await request<{ success: boolean; data: { user: any; token: string } }>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    login: async (data: { email: string; password: string }) => {
      const res = await request<{ success: boolean; data: { user: any; token: string } }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    me: () => request<{ success: boolean; data: any }>('/auth/me'),
    
    checkEmail: async (email: string) => {
      const res = await request<{ success: boolean; data: { available: boolean } }>('/auth/check-email', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return res.data.available;
    },

    updateEmail: async (email: string) => {
      const res = await request<{ success: boolean; data: { email: string } }>('/users/me/email', {
        method: 'PUT',
        body: JSON.stringify({ email }),
      });
      return res.data.email;
    },

    getSecurityStatus: async () => {
      const res = await request<{ success: boolean; data: { hasPassword: boolean; hasPasskey: boolean } }>('/auth/security');
      return res.data;
    },

    updatePassword: async (newPassword: string, currentPassword?: string) => {
      const res = await request<{ success: boolean; data: { hasPassword: boolean } }>('/auth/password', {
        method: 'POST',
        body: JSON.stringify({ newPassword, currentPassword }),
      });
      return res.data;
    },

    removePassword: async () => {
      const res = await request<{ success: boolean; data: { hasPassword: boolean } }>('/auth/password', {
        method: 'DELETE',
      });
      return res.data;
    },
    
    getPasskeyOptions: async (email: string) => {
      const res = await request<{ success: boolean; data: any }>('/auth/passkey/auth-options', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return res.data;
    },
    
    verifyPasskey: async (data: { 
      email: string; 
      credential?: any;
      credentialId?: string;
      authenticatorData?: string;
      clientDataJSON?: string;
      signature?: string;
    }) => {
      // If raw fields provided, wrap into credential object format expected by @simplewebauthn
      const payload = data.credential ? data : {
        email: data.email,
        credential: {
          id: data.credentialId,
          rawId: data.credentialId,
          type: 'public-key',
          response: {
            authenticatorData: data.authenticatorData,
            clientDataJSON: data.clientDataJSON,
            signature: data.signature,
          },
        },
      };
      
      const res = await request<{ success: boolean; data: { user: any; token: string } }>('/auth/passkey/auth-verify', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res.data;
    },
    
    getPasskeyRegistrationOptions: async (email?: string) => {
      const payload = email ? { email } : undefined;
      const res = await request<{ success: boolean; data: any }>('/auth/passkey/register-options', {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      });
      return res.data;
    },
    
    registerPasskey: async (data: { 
      email?: string;
      credential?: any;
      credentialId?: string;
      publicKey?: string;
      attestationObject?: string;
      clientDataJSON?: string;
      deviceName?: string;
    }) => {
      // If raw fields provided, wrap into credential object format expected by @simplewebauthn
      const payload = data.credential ? { ...data, email: data.email } : {
        credential: {
          id: data.credentialId,
          rawId: data.credentialId,
          type: 'public-key',
          response: {
            attestationObject: data.attestationObject,
            clientDataJSON: data.clientDataJSON,
            publicKey: data.publicKey,
          },
        },
        deviceName: data.deviceName,
        email: data.email,
      };
      
      await request<{ success: boolean }>('/auth/passkey/register-verify', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  },

  users: {
    getProfile: async (identifier: string) => {
      const res = await request<{ success: boolean; data: any }>(`/users/${identifier}`);
      return res.data;
    },
    
    getByHandle: async (handle: string) => {
      const res = await request<{ success: boolean; data: any }>(`/users/@${handle}`);
      return res.data;
    },
    
    updateProfile: async (data: any) => {
      const res = await request<{ success: boolean; data: any }>('/users/me', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    update: async (data: any) => {
      const res = await request<{ success: boolean; data: any }>('/users/me', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    updateAvatar: async (mediaId: string) => {
      await request<{ success: boolean }>('/users/me/avatar', {
        method: 'PUT',
        body: JSON.stringify({ mediaId }),
      });
    },
    
    getUserPosts: async (identifier: string, page = 1) => {
      const res = await request<{ success: boolean; data: any }>(`/users/${identifier}/posts`, {
        params: { page: String(page) },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizePost) : [];
    },
    
    getPosts: async (identifier: string, page = 1) => {
      const res = await request<{ success: boolean; data: any }>(`/users/@${identifier}/posts`, {
        params: { page: String(page) },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizePost) : [];
    },
    
    follow: async (userId: string) => {
      await request<{ success: boolean }>(`/users/${userId}/follow`, {
        method: 'POST',
      });
    },
    
    unfollow: async (userId: string) => {
      await request<{ success: boolean }>(`/users/${userId}/unfollow`, {
        method: 'POST',
      });
    },
    
    search: async (params: { query: string }) => {
      const res = await request<{ success: boolean; data: { items: any[] } }>('/users/search', {
        params: { q: params.query },
      });
      return res.data.items;
    },
    
    deleteAccount: async () => {
      await request<{ success: boolean }>('/users/me', {
        method: 'DELETE',
      });
    },
  },

  posts: {
    getFeed: async (params?: { page?: number; segmentId?: string; cursor?: string }) => {
      const res = await request<{ success: boolean; data: any }>('/posts', {
        params: {
          page: params?.page?.toString(),
          segmentId: params?.segmentId,
          cursor: params?.cursor,
        },
      });
      if (Array.isArray(res.data)) {
        return res.data.map(normalizePost);
      }
      if (Array.isArray(res.data?.items)) {
        return { ...res.data, items: res.data.items.map(normalizePost) };
      }
      if (Array.isArray(res.data?.posts)) {
        return { ...res.data, posts: res.data.posts.map(normalizePost) };
      }
      return res.data;
    },
    
    getById: async (id: string) => {
      const res = await request<{ success: boolean; data: any }>(`/posts/${id}`);
      return normalizePost(res.data);
    },
    
    create: async (data: { content?: string; segmentId: string; mediaIds?: string[]; attachWeather?: boolean }) => {
      const res = await request<{ success: boolean; data: any }>('/posts', {
        method: 'POST',
        body: JSON.stringify({
          bodyText: data.content,
          segmentId: data.segmentId,
          mediaIds: data.mediaIds,
          attachWeather: data.attachWeather,
        }),
      });
      return normalizePost(res.data);
    },
    
    delete: async (id: string) => {
      await request<{ success: boolean }>(`/posts/${id}`, {
        method: 'DELETE',
      });
    },
    
    react: async (id: string, params: { type?: string }) => {
      const res = await request<{ success: boolean; data: { reacted: boolean } }>(`/posts/${id}/react`, {
        method: 'POST',
        body: JSON.stringify({ type: params.type || 'like' }),
      });
      return res.data;
    },
    
    reactToPost: async (id: string, type = 'like') => {
      const res = await request<{ success: boolean; data: { reacted: boolean } }>(`/posts/${id}/react`, {
        method: 'POST',
        body: JSON.stringify({ type }),
      });
      return res.data;
    },
    
    getComments: async (postId: string, page = 1) => {
      const res = await request<{ success: boolean; data: any }>(`/posts/${postId}/comments`, {
        params: { page: String(page) },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizeComment) : [];
    },
    
    addComment: async (postId: string, data: string | { content: string; parentId?: string }) => {
      const bodyText = typeof data === 'string' ? data : data.content;
      const parentId = typeof data === 'string' ? undefined : data.parentId;
      const res = await request<{ success: boolean; data: any }>(`/posts/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ bodyText, parentId }),
      });
      return normalizeComment(res.data);
    },
    
    getCommentReplies: async (postId: string, commentId: string) => {
      const res = await request<{ success: boolean; data: any }>(`/posts/${postId}/comments/${commentId}/replies`);
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizeComment) : [];
    },
  },

  waterways: {
    getWaterways: async (options?: { geometry?: boolean }) => {
      const params = options?.geometry ? '?geometry=true' : '';
      const res = await request<{ success: boolean; data: any[] }>(`/waterways${params}`);
      return res.data;
    },
    
    getAll: async (options?: { geometry?: boolean }) => {
      const params = options?.geometry ? '?geometry=true' : '';
      const res = await request<{ success: boolean; data: any[] }>(`/waterways${params}`);
      return res.data;
    },

    getGeoJSON: async (options?: { includeCanalplan?: boolean; includeOsm?: boolean }): Promise<GeoJSON.FeatureCollection> => {
      const params = new URLSearchParams();
      if (typeof options?.includeCanalplan === 'boolean') {
        params.set('includeCanalplan', options.includeCanalplan ? 'true' : 'false');
      }
      if (typeof options?.includeOsm === 'boolean') {
        params.set('includeOsm', options.includeOsm ? 'true' : 'false');
      }
      const qs = params.toString();
      const url = `${API_BASE}/waterways/geojson${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch waterway GeoJSON');
      const data = await res.json();
      console.log(`[MapData] Loaded ${data.features?.length ?? 0} waterway features`);
      return data;
    },

    getOsmWaterwaysGeoJSON: async (options?: { limit?: number; includeManual?: boolean; bbox?: { south: number; west: number; north: number; east: number }; signal?: AbortSignal }): Promise<GeoJSON.FeatureCollection> => {
      const params = new URLSearchParams();
      if (typeof options?.limit === 'number' && Number.isFinite(options.limit)) {
        params.set('limit', String(Math.floor(options.limit)));
      }
      if (typeof options?.includeManual === 'boolean') {
        params.set('includeManual', options.includeManual ? 'true' : 'false');
      }
      if (options?.bbox) {
        const { south, west, north, east } = options.bbox;
        params.set('bbox', `${south},${west},${north},${east}`);
      }
      const qs = params.toString();
      const url = `${API_BASE}/waterways/osm-waterways/geojson${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { cache: 'no-store', signal: options?.signal });
      if (!res.ok) throw new Error('Failed to fetch OSM waterways GeoJSON');
      const data = await res.json();
      console.log(`[MapData] Loaded ${data.features?.length ?? 0} OSM waterway features`);
      return data;
    },

    importOsmWaterways: async (): Promise<{
      mode: 'replace' | 'upsert' | 'update-existing';
      fetchedElementCount: number;
      parsedFeatureCount: number;
      validFeatureCount: number;
      insertedCount: number;
      updatedCount: number;
      ignoredNewCount: number;
      durationMs: number;
    }> => {
      const res = await request<{
        success: boolean;
        data: {
          mode: 'replace' | 'upsert' | 'update-existing';
          fetchedElementCount: number;
          parsedFeatureCount: number;
          validFeatureCount: number;
          insertedCount: number;
          updatedCount: number;
          ignoredNewCount: number;
          durationMs: number;
        };
      }>('/waterways/osm-waterways/import', {
        method: 'POST',
      });
      return res.data;
    },

    importOsmBridges: async (): Promise<{
      mode: 'replace' | 'upsert';
      fetchedElementCount: number;
      parsedFeatureCount: number;
      validFeatureCount: number;
      insertedCount: number;
      updatedCount: number;
      removedNonIntersectingCount: number;
      deletedBeforeImportCount: number;
      durationMs: number;
    }> => {
      const res = await request<{
        success: boolean;
        data: {
          mode: 'replace' | 'upsert';
          fetchedElementCount: number;
          parsedFeatureCount: number;
          validFeatureCount: number;
          insertedCount: number;
          updatedCount: number;
          removedNonIntersectingCount: number;
          deletedBeforeImportCount: number;
          durationMs: number;
        };
      }>('/waterways/osm-bridges/import', {
        method: 'POST',
      });
      return res.data;
    },

    importOsmLocks: async (): Promise<{
      mode: 'replace' | 'upsert';
      fetchedElementCount: number;
      parsedFeatureCount: number;
      validFeatureCount: number;
      insertedCount: number;
      updatedCount: number;
      removedNonNearbyCount: number;
      deletedBeforeImportCount: number;
      durationMs: number;
    }> => {
      const res = await request<{
        success: boolean;
        data: {
          mode: 'replace' | 'upsert';
          fetchedElementCount: number;
          parsedFeatureCount: number;
          validFeatureCount: number;
          insertedCount: number;
          updatedCount: number;
          removedNonNearbyCount: number;
          deletedBeforeImportCount: number;
          durationMs: number;
        };
      }>('/waterways/osm-locks/import', {
        method: 'POST',
      });
      return res.data;
    },

    importOsmWeirs: async (): Promise<{
      fetchedElementCount: number;
      parsedFeatureCount: number;
      validFeatureCount: number;
      insertedCount: number;
      removedByBboxCount: number;
      removedByExactDistanceCount: number;
      removedNonNearbyCount: number;
      deletedBeforeImportCount: number;
      durationMs: number;
    }> => {
      const res = await request<{
        success: boolean;
        data: {
          fetchedElementCount: number;
          parsedFeatureCount: number;
          validFeatureCount: number;
          insertedCount: number;
          removedByBboxCount: number;
          removedByExactDistanceCount: number;
          removedNonNearbyCount: number;
          deletedBeforeImportCount: number;
          durationMs: number;
        };
      }>('/waterways/osm-weirs/import', {
        method: 'POST',
      });
      return res.data;
    },

    getOsmBridgesGeoJSON: async (options?: { limit?: number; bbox?: { south: number; west: number; north: number; east: number }; signal?: AbortSignal }): Promise<GeoJSON.FeatureCollection> => {
      const params = new URLSearchParams();
      if (typeof options?.limit === 'number' && Number.isFinite(options.limit)) {
        params.set('limit', String(Math.floor(options.limit)));
      }
      if (options?.bbox) {
        const { south, west, north, east } = options.bbox;
        params.set('bbox', `${south},${west},${north},${east}`);
      }
      const qs = params.toString();
      const url = `${API_BASE}/waterways/osm-bridges/geojson${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { cache: 'no-store', signal: options?.signal });
      if (!res.ok) throw new Error('Failed to fetch OSM bridges GeoJSON');
      const data = await res.json();
      console.log(`[MapData] Loaded ${data.features?.length ?? 0} OSM bridge features`);
      return data;
    },

    getOsmLocksGeoJSON: async (options?: { limit?: number; bbox?: { south: number; west: number; north: number; east: number }; signal?: AbortSignal }): Promise<GeoJSON.FeatureCollection> => {
      const params = new URLSearchParams();
      if (typeof options?.limit === 'number' && Number.isFinite(options.limit)) {
        params.set('limit', String(Math.floor(options.limit)));
      }
      if (options?.bbox) {
        const { south, west, north, east } = options.bbox;
        params.set('bbox', `${south},${west},${north},${east}`);
      }
      const qs = params.toString();
      const url = `${API_BASE}/waterways/osm-locks/geojson${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { cache: 'no-store', signal: options?.signal });
      if (!res.ok) throw new Error('Failed to fetch OSM locks GeoJSON');
      const data = await res.json();
      console.log(`[MapData] Loaded ${data.features?.length ?? 0} OSM lock features`);
      return data;
    },

    getOsmWeirsGeoJSON: async (options?: { limit?: number; bbox?: { south: number; west: number; north: number; east: number } }): Promise<GeoJSON.FeatureCollection> => {
      const params = new URLSearchParams();
      if (typeof options?.limit === 'number' && Number.isFinite(options.limit)) {
        params.set('limit', String(Math.floor(options.limit)));
      }
      if (options?.bbox) {
        const { south, west, north, east } = options.bbox;
        params.set('bbox', `${south},${west},${north},${east}`);
      }
      const qs = params.toString();
      const url = `${API_BASE}/waterways/osm-weirs/geojson${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch OSM weirs GeoJSON');
      const data = await res.json();
      console.log(`[MapData] Loaded ${data.features?.length ?? 0} OSM weir features`);
      return data;
    },

    planRoute: async (
      requestBody: NavigationRoutePlanRequest,
      options?: { signal?: AbortSignal },
    ): Promise<NavigationRoutePlanResponse> => {
      const res = await request<{
        success: boolean;
        data?: NavigationRoutePlanResponse;
        error?: string;
      }>('/waterways/routes/plan', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        signal: options?.signal,
      });
      if (res.data) return res.data;
      return {
        ok: false,
        routingMode: 'server',
        graphVersion: 'unknown',
        timingsMs: { snap: 0, solve: 0, total: 0 },
        route: null,
        error: res.error || 'Route planning failed.',
      };
    },

    getRouteGraphStatus: async (): Promise<RouteGraphStatusResponse> => {
      const res = await request<{
        success: boolean;
        data: RouteGraphStatusResponse;
      }>('/waterways/routes/graph-status');
      return res.data;
    },

    searchOsmFeatures: async (params: {
      query: string;
      limit?: number;
    }): Promise<OSMFeatureSearchResult[]> => {
      const queryText = params.query.trim();
      if (!queryText) return [];

      const queryParams: Record<string, string> = { q: queryText };
      if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
        queryParams.limit = String(Math.floor(params.limit));
      }

      const res = await request<{ success: boolean; data: any[] }>('/waterways/osm-features/search', {
        params: queryParams,
      });

      if (!Array.isArray(res.data)) return [];
      return res.data
        .map((entry): OSMFeatureSearchResult | null => {
          if (!entry || typeof entry !== 'object') return null;
          const dataset = String(entry.dataset || '').trim() as OsmFeatureDataset;
          if (dataset !== 'osm' && dataset !== 'osm_bridges' && dataset !== 'osm_locks' && dataset !== 'osm_weirs') {
            return null;
          }

          const featureType = String(entry.featureType || '').trim() as OsmFeatureType;
          if (featureType !== 'waterway' && featureType !== 'bridge' && featureType !== 'lock' && featureType !== 'weir') {
            return null;
          }

          const id = String(entry.id || '').trim();
          const name = String(entry.name || '').trim();
          const latitude = Number(entry.latitude);
          const longitude = Number(entry.longitude);
          if (!id || !name || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return null;
          }

          const rawSubtype = entry.subtype;
          return {
            id,
            name,
            dataset,
            featureType,
            subtype: typeof rawSubtype === 'string' && rawSubtype.trim() ? rawSubtype.trim() : null,
            latitude,
            longitude,
          };
        })
        .filter((entry): entry is OSMFeatureSearchResult => Boolean(entry));
    },

    getOsmReferenceGeoJSON: async (
      params: {
        south: number;
        west: number;
        north: number;
        east: number;
        canals: boolean;
        rivers: boolean;
      },
      options?: { signal?: AbortSignal },
    ): Promise<GeoJSON.FeatureCollection> => {
      const bbox = [
        params.south.toFixed(6),
        params.west.toFixed(6),
        params.north.toFixed(6),
        params.east.toFixed(6),
      ].join(',');
      const res = await request<{ success: boolean; data: GeoJSON.FeatureCollection }>('/waterways/osm-reference', {
        params: {
          bbox,
          canals: String(params.canals),
          rivers: String(params.rivers),
        },
        signal: options?.signal,
      });
      if (res.data?.type === 'FeatureCollection' && Array.isArray(res.data.features)) {
        return res.data;
      }
      return { type: 'FeatureCollection', features: [] };
    },
    
    getSegments: async (
      waterwayId?: string,
      options?: { includeCanalplan?: boolean },
    ) => {
      const queryParams: Record<string, string> = {};
      if (typeof options?.includeCanalplan === 'boolean') {
        queryParams.includeCanalplan = options.includeCanalplan ? 'true' : 'false';
      }

      if (waterwayId) {
        const res = await request<{ success: boolean; data: any[] }>(`/waterways/${waterwayId}/segments`, {
          params: queryParams,
        });
        return Array.isArray(res.data) ? res.data.map(normalizeSegment) : [];
      }
      const res = await request<{ success: boolean; data: any[] }>('/waterways/segments', {
        params: queryParams,
      });
      return Array.isArray(res.data) ? res.data.map(normalizeSegment) : [];
    },
    
    getAllSegments: async () => {
      const res = await request<{ success: boolean; data: any[] }>('/waterways/segments');
      return Array.isArray(res.data) ? res.data.map(normalizeSegment) : [];
    },
    
    getSegment: async (id: string) => {
      const res = await request<{ success: boolean; data: any }>(`/waterways/segments/${id}`);
      return normalizeSegment(res.data);
    },
    
    searchSegments: async (params: { query: string }) => {
      const res = await request<{ success: boolean; data: any[] }>('/waterways/segments/search', {
        params: { q: params.query },
      });
      return Array.isArray(res.data) ? res.data.map(normalizeSegment) : [];
    },
    
    getSegmentPosts: async (segmentId: string) => {
      const res = await request<{ success: boolean; data: any }>(`/waterways/segments/${segmentId}/posts`);
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizePost) : [];
    },

    deleteWaterway: async (waterwayId: string): Promise<{ success: boolean; message: string }> => {
      return request<{ success: boolean; message: string }>(`/waterways/${waterwayId}`, {
        method: 'DELETE',
      });
    },

    updateWaterway: async (
      waterwayId: string,
      data: { name?: string; type?: string; tags?: Record<string, string> },
    ) => {
      const res = await request<{ success: boolean; data: any }>(`/waterways/${waterwayId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    updateGeometry: async (waterwayId: string, geometry: any) => {
      return request<{ success: boolean; message: string }>(`/waterways/${waterwayId}/geometry`, {
        method: 'PUT',
        body: JSON.stringify({ geometry }),
      });
    },

    updateOsmBridge: async (
      bridgeId: string,
      data: { name?: string; tags?: Record<string, string> },
    ) => {
      const res = await request<{ success: boolean; data: any }>(`/waterways/osm-bridges/${bridgeId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    updateOsmBridgeGeometry: async (bridgeId: string, geometry: any) => {
      return request<{ success: boolean; message: string }>(`/waterways/osm-bridges/${bridgeId}/geometry`, {
        method: 'PUT',
        body: JSON.stringify({ geometry }),
      });
    },

    deleteOsmBridge: async (bridgeId: string): Promise<{ success: boolean; message: string }> => {
      return request<{ success: boolean; message: string }>(`/waterways/osm-bridges/${bridgeId}`, {
        method: 'DELETE',
      });
    },

    updateOsmLock: async (
      lockId: string,
      data: { name?: string; tags?: Record<string, string> },
    ) => {
      const res = await request<{ success: boolean; data: any }>(`/waterways/osm-locks/${lockId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    updateOsmLockGeometry: async (lockId: string, geometry: any) => {
      return request<{ success: boolean; message: string }>(`/waterways/osm-locks/${lockId}/geometry`, {
        method: 'PUT',
        body: JSON.stringify({ geometry }),
      });
    },

    deleteOsmLock: async (lockId: string): Promise<{ success: boolean; message: string }> => {
      return request<{ success: boolean; message: string }>(`/waterways/osm-locks/${lockId}`, {
        method: 'DELETE',
      });
    },

    updateOsmWeir: async (
      weirId: string,
      data: { name?: string; tags?: Record<string, string> },
    ) => {
      const res = await request<{ success: boolean; data: any }>(`/waterways/osm-weirs/${weirId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    updateOsmWeirGeometry: async (weirId: string, geometry: any) => {
      return request<{ success: boolean; message: string }>(`/waterways/osm-weirs/${weirId}/geometry`, {
        method: 'PUT',
        body: JSON.stringify({ geometry }),
      });
    },

    deleteOsmWeir: async (weirId: string): Promise<{ success: boolean; message: string }> => {
      return request<{ success: boolean; message: string }>(`/waterways/osm-weirs/${weirId}`, {
        method: 'DELETE',
      });
    },

    createWaterway: async (
      data: { name: string; type: string; geometry?: any; tags?: Record<string, string> },
    ) => {
      const res = await request<{ success: boolean; data: any }>('/waterways', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    combineWaterways: async (waterwayIds: string[]): Promise<{ targetId: string; mergedWaterwayIds: string[]; movedSegmentCount: number }> => {
      const res = await request<{ success: boolean; data: { targetId: string; mergedWaterwayIds: string[]; movedSegmentCount: number } }>('/waterways/combine', {
        method: 'POST',
        body: JSON.stringify({ waterwayIds }),
      });
      return res.data;
    },

    startOsmOAuth: async (
      options?: { returnPath?: string },
    ): Promise<{ authorizeUrl: string; expiresInSeconds: number }> => {
      const res = await request<{ success: boolean; data: { authorizeUrl: string; expiresInSeconds: number } }>('/waterways/osm/oauth/start', {
        params: {
          returnPath: options?.returnPath,
        },
      });
      return res.data;
    },

    uploadOsmTags: async (data: {
      accessToken: string;
      comment: string;
      source: string;
      updates: Array<{
        waterwayId: string;
        tags: Record<string, string>;
        removedKeys?: string[];
      }>;
    }): Promise<{
      changesetId: string;
      updated: number;
      failed: Array<{ waterwayId: string; error: string }>;
    }> => {
      const res = await request<{
        success: boolean;
        data: {
          changesetId: string;
          updated: number;
          failed: Array<{ waterwayId: string; error: string }>;
        };
      }>('/waterways/osm/upload-tags', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },
  },

  pois: {
    getGeoJSON: async (options?: { includeCanalplan?: boolean }): Promise<GeoJSON.FeatureCollection> => {
      const params = new URLSearchParams();
      if (typeof options?.includeCanalplan === 'boolean') {
        params.set('includeCanalplan', options.includeCanalplan ? 'true' : 'false');
      }
      const qs = params.toString();
      const url = `${API_BASE}/pois/geojson${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch POI GeoJSON');
      return res.json();
    },
    
    getAll: async (params?: { type?: string; search?: string }) => {
      const queryParams: Record<string, string | undefined> = {};
      if (params?.type) queryParams.type = params.type;
      if (params?.search) queryParams.search = params.search;
      const res = await request<{ success: boolean; data: any[] }>('/pois', { params: queryParams });
      return Array.isArray(res.data) ? res.data : [];
    },
    
    get: async (id: string) => {
      const res = await request<{ success: boolean; data: any }>(`/pois/${id}`);
      return res.data;
    },
    
    create: async (data: { name: string; type: string; latitude: number; longitude: number; description?: string; waterwayId?: string }) => {
      const res = await request<{ success: boolean; data: any }>('/pois', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    update: async (id: string, data: { name?: string; type?: string; latitude?: number; longitude?: number; description?: string }) => {
      const res = await request<{ success: boolean; data: any }>(`/pois/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    delete: async (id: string) => {
      return request<{ success: boolean; message: string }>(`/pois/${id}`, {
        method: 'DELETE',
      });
    },
    
    // POI Types
    getTypes: async () => {
      const res = await request<{ success: boolean; data: any[] }>('/pois/types');
      return Array.isArray(res.data) ? res.data : [];
    },
    
    createType: async (data: { name: string; label: string; svg?: string; color?: string }) => {
      const res = await request<{ success: boolean; data: any }>('/pois/types', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    updateType: async (id: string, data: { name?: string; label?: string; svg?: string; color?: string }) => {
      const res = await request<{ success: boolean; data: any }>(`/pois/types/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.data;
    },
    
    deleteType: async (id: string) => {
      return request<{ success: boolean; message: string }>(`/pois/types/${id}`, {
        method: 'DELETE',
      });
    },

    getIconSizing: async () => {
      const res = await request<{ success: boolean; data: any[] }>('/pois/icon-sizing');
      return Array.isArray(res.data) ? res.data : [];
    },

    updateIconSizing: async (rules: Array<{ zoomFrom: number; zoomTo: number; svgSizePx: number }>) => {
      const res = await request<{ success: boolean; data: any[] }>('/pois/icon-sizing', {
        method: 'PUT',
        body: JSON.stringify({ rules }),
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  },

  chats: {
    getChats: async () => {
      const res = await request<{ success: boolean; data: any[] }>('/chats');
      return Array.isArray(res.data) ? res.data.map(normalizeChat) : [];
    },
    
    getAll: async () => {
      const res = await request<{ success: boolean; data: any[] }>('/chats');
      return Array.isArray(res.data) ? res.data.map(normalizeChat) : [];
    },
    
    create: async (data: { type: 'dm' | 'group'; participantIds: string[]; name?: string }) => {
      let createdId: string | undefined;
      if (data.type === 'dm') {
        const res = await request<{ success: boolean; data: any }>('/chats/dm', {
          method: 'POST',
          body: JSON.stringify({ participantId: data.participantIds[0] }),
        });
        createdId = res.data?.chatId || res.data?.id;
      } else {
        const res = await request<{ success: boolean; data: any }>('/chats/group', {
          method: 'POST',
          body: JSON.stringify({ participantIds: data.participantIds, name: data.name }),
        });
        createdId = res.data?.chatId || res.data?.id;
      }
      if (createdId) {
        const chatsRes = await request<{ success: boolean; data: any[] }>('/chats');
        const chat = Array.isArray(chatsRes.data) ? chatsRes.data.find((item) => item.id === createdId) : null;
        if (chat) return normalizeChat(chat);
      }
      return normalizeChat({
        id: createdId || '',
        type: data.type,
        name: data.name,
        participants: [],
        unreadCount: 0,
      });
    },
    
    createDM: async (participantId: string) => {
      const res = await request<{ success: boolean; data: { chatId: string; isNew: boolean } }>('/chats/dm', {
        method: 'POST',
        body: JSON.stringify({ participantId }),
      });
      return res.data;
    },
    
    inviteToChat: async (chatId: string, userIds: string[]) => {
      const res = await request<{ success: boolean; data: any }>(`/chats/${chatId}/invite`, {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      });
      return res.data;
    },
    
    getMessages: async (chatId: string, before?: string) => {
      const res = await request<{ success: boolean; data: any[] }>(`/chats/${chatId}/messages`, {
        params: before ? { before } : {},
      });
      return Array.isArray(res.data) ? res.data.map(normalizeMessage) : [];
    },
    
    sendMessage: async (chatId: string, content: string | { content: string }, mediaId?: string) => {
      const bodyText = typeof content === 'string' ? content : content.content;
      const res = await request<{ success: boolean; data: any }>(`/chats/${chatId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ bodyText, mediaId }),
      });
      return normalizeMessage(res.data);
    },
    
    leaveChat: async (chatId: string) => {
      await request<{ success: boolean }>(`/chats/${chatId}/leave`, {
        method: 'POST',
      });
    },
  },

  notifications: {
    getAll: async (params?: { unreadOnly?: boolean; page?: number }) => {
      const res = await request<{ success: boolean; data: any }>('/notifications', {
        params: {
          page: params?.page?.toString() || '1',
          unreadOnly: params?.unreadOnly?.toString(),
        },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizeNotification) : [];
    },
    
    getNotifications: async (page = 1, unreadOnly = false) => {
      const res = await request<{ success: boolean; data: any }>('/notifications', {
        params: { page: String(page), unreadOnly: String(unreadOnly) },
      });
      return {
        ...res.data,
        items: Array.isArray(res.data?.items) ? res.data.items.map(normalizeNotification) : [],
      };
    },
    
    markAsRead: async (id: string) => {
      await request<{ success: boolean }>(`/notifications/${id}/read`, {
        method: 'POST',
      });
    },
    
    markRead: async (id: string) => {
      await request<{ success: boolean }>(`/notifications/${id}/read`, {
        method: 'POST',
      });
    },
    
    markAllAsRead: async () => {
      await request<{ success: boolean }>('/notifications/read-all', {
        method: 'POST',
      });
    },
    
    markAllRead: async () => {
      await request<{ success: boolean }>('/notifications/read-all', {
        method: 'POST',
      });
    },
    
    getPreferences: async () => {
      const res = await request<{ success: boolean; data: any }>('/notifications/preferences');
      return normalizeNotificationPreferences(res.data);
    },
    
    updatePreferences: async (data: any) => {
      const payload = toNotificationPrefsPayload(data);
      const res = await request<{ success: boolean; data: any }>('/notifications/preferences', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return normalizeNotificationPreferences(res.data);
    },
  },

  reports: {
    create: async (data: { targetType: string; targetId: string; reason: string; note?: string }) => {
      const res = await request<{ success: boolean; data: { id: string } }>('/reports', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },
  },

  media: {
    upload: async (file: File) => {
      const token = useAuthStore.getState().token;
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE}/media/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      
      const data = await response.json();
      return { id: data.data?.id, url: data.data?.url, status: data.data?.status };
    },
    
    getMedia: async (id: string) => {
      const res = await request<{ success: boolean; data: any }>(`/media/${id}`);
      return res.data;
    },
    
    deleteMedia: async (id: string) => {
      await request<{ success: boolean }>(`/media/${id}`, {
        method: 'DELETE',
      });
    },
  },

  ads: {
    getAds: async (type?: 'banner' | 'native', limit = 5) => {
      const res = await request<{ success: boolean; data: any }>('/ads', {
        params: { ...(type && { type }), limit: String(limit) },
      });
      return res.data;
    },
    
    trackImpression: async (adId: string, placement?: string) => {
      await request<{ success: boolean }>(`/ads/${adId}/impression`, {
        method: 'POST',
        body: JSON.stringify({ placement }),
      });
    },
    
    trackClick: async (adId: string) => {
      await request<{ success: boolean }>(`/ads/${adId}/click`, {
        method: 'POST',
      });
    },
    
    getSubscription: async () => {
      const res = await request<{ success: boolean; data: any }>('/ads/subscription');
      return res.data;
    },
    
    createSubscription: async (paymentToken?: string) => {
      const res = await request<{ success: boolean; data: any }>('/ads/subscription', {
        method: 'POST',
        body: JSON.stringify({ paymentToken }),
      });
      return res.data;
    },
    
    cancelSubscription: async () => {
      await request<{ success: boolean }>('/ads/subscription', {
        method: 'DELETE',
      });
    },
  },

  admin: {
    getStats: async () => {
      const res = await request<{ success: boolean; data: any }>('/admin/stats');
      return res.data;
    },
    
    getModerationQueue: async (page = 1) => {
      const res = await request<{ success: boolean; data: any }>('/admin/moderation/posts', {
        params: { page: String(page) },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items : [];
    },
    
    approveContent: async (id: string) => {
      await request<{ success: boolean }>(`/admin/moderation/posts/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'approve' }),
      });
    },
    
    rejectContent: async (id: string, data?: { reason?: string }) => {
      await request<{ success: boolean }>(`/admin/moderation/posts/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'reject', reason: data?.reason }),
      });
    },
    
    moderatePost: async (id: string, action: 'approve' | 'reject', reason?: string) => {
      await request<{ success: boolean }>(`/admin/moderation/posts/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action, reason }),
      });
    },
    
    getReports: async (params?: { status?: string; page?: number }) => {
      const res = await request<{ success: boolean; data: any }>('/admin/reports', {
        params: {
          status: params?.status,
          page: params?.page?.toString() || '1',
        },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items : [];
    },
    
    resolveReport: async (id: string, data?: { action?: string }) => {
      await request<{ success: boolean }>(`/admin/reports/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action: data?.action || 'resolve' }),
      });
    },
    
    handleReport: async (id: string, action: string, reason?: string) => {
      await request<{ success: boolean }>(`/admin/reports/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action, reason }),
      });
    },
    
    getUsers: async (params?: { page?: number; search?: string; status?: string }) => {
      const res = await request<{ success: boolean; data: any }>('/admin/users', {
        params: {
          page: params?.page?.toString() || '1',
          search: params?.search,
          status: params?.status,
        },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizeAdminUser) : [];
    },
    
    warnUser: async (id: string, data?: { message?: string }) => {
      await request<{ success: boolean }>(`/admin/users/${id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action: 'warn', reason: data?.message }),
      });
    },
    
    suspendUser: async (id: string, data?: { duration?: number | string }) => {
      await request<{ success: boolean }>(`/admin/users/${id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action: 'suspend', duration: data?.duration }),
      });
    },
    
    banUser: async (id: string, data?: { reason?: string }) => {
      await request<{ success: boolean }>(`/admin/users/${id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action: 'ban', reason: data?.reason }),
      });
    },
    
    unbanUser: async (id: string) => {
      await request<{ success: boolean }>(`/admin/users/${id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action: 'unban' }),
      });
    },
    
    userAction: async (id: string, action: 'warn' | 'ban' | 'unban', reason?: string) => {
      await request<{ success: boolean }>(`/admin/users/${id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action, reason }),
      });
    },
    
    featurePost: async (id: string, featured: boolean, pinned: boolean) => {
      await request<{ success: boolean }>(`/admin/posts/${id}/feature`, {
        method: 'POST',
        body: JSON.stringify({ featured, pinned }),
      });
    },
    
    getFeaturingRequests: async () => {
      const res = await request<{ success: boolean; data: any }>('/admin/featuring-requests');
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items : [];
    },
    
    getAuditLog: async (params?: { action?: string; search?: string; page?: number }) => {
      const res = await request<{ success: boolean; data: any }>('/admin/audit-log', {
        params: {
          action: params?.action,
          search: params?.search,
          page: params?.page?.toString() || '1',
        },
      });
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizeAdminAuditEntry) : [];
    },
    
    getAds: async () => {
      const res = await request<{ success: boolean; data: any }>('/admin/ads');
      const items = res.data?.items || res.data;
      return Array.isArray(items) ? items.map(normalizeAdminAd) : [];
    },
    
    createAd: async (data: { type: string; title: string; content?: string; description?: string; imageUrl?: string; targetUrl: string; budget?: number }) => {
      const res = await request<{ success: boolean; data: { id: string } }>('/admin/ads', {
        method: 'POST',
        body: JSON.stringify({
          type: data.type,
          title: data.title,
          bodyText: data.content ?? data.description,
          imageUrl: data.imageUrl,
          targetUrl: data.targetUrl,
        }),
      });
      return res.data;
    },
    
    updateAd: async (id: string, data: { status?: string }) => {
      await request<{ success: boolean }>(`/admin/ads/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },
    
    deleteAd: async (id: string) => {
      await request<{ success: boolean }>(`/admin/ads/${id}`, {
        method: 'DELETE',
      });
    },
  },

  follows: {
    follow: async (userId: string) => {
      await request<{ success: boolean }>(`/users/${userId}/follow`, {
        method: 'POST',
      });
    },
    
    unfollow: async (userId: string) => {
      await request<{ success: boolean }>(`/users/${userId}/unfollow`, {
        method: 'POST',
      });
    },
    
    checkFollowing: async (userId: string) => {
      const res = await request<{ success: boolean; data: { isFollowing: boolean } }>(`/users/${userId}/following`);
      return res.data;
    },
  },

  search: {
    users: async (query: string) => {
      const res = await request<{ success: boolean; data: { items: any[] } }>('/search/users', {
        params: { q: query },
      });
      return res.data;
    },
  },
};

// Legacy exports for backward compatibility
export const authApi = api.auth;
export const usersApi = api.users;
export const postsApi = api.posts;
export const waterwaysApi = api.waterways;
export const chatsApi = api.chats;
export const notificationsApi = api.notifications;
export const reportsApi = api.reports;
export const mediaApi = api.media;
export const adsApi = api.ads;
export const adminApi = api.admin;
